import { useDispatch } from 'react-redux'
import { useSelector } from '@state/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid } from '@mui/material'
import { TextInput, SelectInput } from '@components/inputs'
import {
  CreateRequestingOrganisationDto,
  RequestingOrganisationDto,
} from '@services/RequestingOrganisationsService'
import { useMemo, useEffect } from 'react'
import {
  createOrganisation,
  updateOrganisation,
} from '@state/thunks/organisationThunk'
import { addOrganisationSchema } from '@utils/schemas'

type Props = {
  row?: RequestingOrganisationDto
  onClose?: () => void
}

type FormValues = {
  name: string
  address: string
  zipCode: string
  city: string
  siret?: string | null
  billingEmail?: string | null
  chorusEnabled: number
  chorusServiceCode?: string | null
  chorusEngagementNumber?: string | null
  siteId: number
}

export function OrganisationForm({ row, onClose }: Props) {
  const dispatch = useDispatch()
  const sites = useSelector(({ site }) => site.sites.datas)

  const {
    formState: { errors },
    control,
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(addOrganisationSchema) as any,
    defaultValues: {
      name: row?.name ?? '',
      address: row?.address ?? '',
      zipCode: row?.zipCode ?? '',
      city: row?.city ?? '',
      siret: row?.siret ?? null,
      billingEmail: row?.billingEmail ?? null,
      chorusEnabled: row?.chorusEnabled ? 1 : 0,
      chorusServiceCode: row?.chorusServiceCode ?? null,
      chorusEngagementNumber: row?.chorusEngagementNumber ?? null,
      siteId: row?.siteId ?? (undefined as unknown as number),
    },
  })

  const chorusEnabled = watch('chorusEnabled')

  useEffect(() => {
    if (!chorusEnabled) {
      setValue('chorusServiceCode', null)
      setValue('chorusEngagementNumber', null)
    }
  }, [chorusEnabled, setValue])

  const sitesOptions = useMemo(
    () => sites.map((s) => ({ label: s.label, value: s.id })),
    [sites],
  )

  const chorusOptions = [
    { label: 'Oui', value: 1 },
    { label: 'Non', value: 0 },
  ]

  const onSubmit = (values: FormValues) => {
    const chorusEnabledBool = Boolean(values.chorusEnabled)
    if (row?.id) {
      const body: Partial<CreateRequestingOrganisationDto> = {
        name: values.name,
        address: values.address,
        zipCode: values.zipCode,
        city: values.city,
        siret: values.siret,
        billingEmail: values.billingEmail,
        chorusEnabled: chorusEnabledBool,
        chorusServiceCode: chorusEnabledBool ? values.chorusServiceCode : null,
        chorusEngagementNumber: chorusEnabledBool
          ? values.chorusEngagementNumber
          : null,
        siteId: values.siteId,
      }
      dispatch(updateOrganisation({ id: row.id, body }))
    } else {
      const body: CreateRequestingOrganisationDto = {
        name: values.name,
        address: values.address,
        zipCode: values.zipCode,
        city: values.city,
        siret: values.siret,
        billingEmail: values.billingEmail,
        chorusEnabled: chorusEnabledBool,
        chorusServiceCode: chorusEnabledBool ? values.chorusServiceCode : null,
        chorusEngagementNumber: chorusEnabledBool
          ? values.chorusEngagementNumber
          : null,
        siteId: values.siteId,
      }
      dispatch(createOrganisation(body))
    }
    if (onClose) onClose()
  }

  return (
    <Grid
      container
      spacing={2}
      alignItems="flex-start"
      sx={{ minWidth: 600, padding: '8px 0px' }}
    >
      <Grid item xs={12} md={6}>
        <SelectInput
          name="siteId"
          options={sitesOptions}
          control={control}
          errors={errors}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="name" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="address" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="zipCode" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="city" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="siret" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="billingEmail" control={control} errors={errors} />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectInput
          name="chorusEnabled"
          options={chorusOptions}
          control={control}
          errors={errors}
        />
      </Grid>
      {Boolean(chorusEnabled) && (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <TextInput
            name="chorusServiceCode"
            control={control}
            errors={errors}
          />
          <TextInput
            name="chorusEngagementNumber"
            control={control}
            errors={errors}
          />
        </Grid>
      )}
      <Grid container justifyContent="flex-end" gap={2} mt={2}>
        {onClose && (
          <Button onClick={onClose} variant="outlined">
            Annuler
          </Button>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="secondary"
        >
          Sauvegarder
        </Button>
      </Grid>
    </Grid>
  )
}
